import { Injectable } from '@angular/core';

const logger = console;

/**
 * Callback invoked whenever the page's visibility changes.
 * @param isHidden - true when the page has become hidden, false when it has become visible
 */
export type VisibilityListener = (isHidden: boolean) => void;

/**
 * Notifies registered listeners when the page is hidden or shown, using the browser Page
 * Visibility API.
 *
 * The service is a root singleton. Listening must be started explicitly with
 * {@link PageVisibilityService.startListeningForPageVisibility} before any listener is called.
 *
 * @see https://www.html5rocks.com/en/tutorials/pagevisibility/intro/
 */
@Injectable({
  providedIn: 'root'
})
export class PageVisibilityService {

  private visibilityListeners: VisibilityListener[] = [];

  private evtname: string | null = null;

  /**
   * Whether the page is currently hidden.
   * @returns true when the page is hidden, false when it is visible
   */
  isHidden(): boolean {
    return document.hidden;
  }

  /**
   * Handles the browser visibility event and fans it out to the registered listeners.
   *
   * Declared as a bound field so the same reference can be added and removed from the document,
   * and so `this` still refers to the service when the browser invokes it.
   */
  private visChange = (): void => {
    const hidden = this.isHidden();

    for (const listener of this.visibilityListeners) {
      listener(hidden);
    }
  };

  /**
   * Starts listening for page visibility changes. Calling this more than once has no effect.
   * @returns nothing
   */
  startListeningForPageVisibility(): void {
    if (this.evtname !== null) { // done init, skip.
      return;
    }

    if (typeof document.hidden === 'undefined') {
      logger.error('Visibility API not supported by browser.');
      return;
    }

    this.evtname = 'visibilitychange';
    document.addEventListener(this.evtname, this.visChange);
  }

  /**
   * Stops listening for page visibility changes. Registered listeners are kept, so listening can
   * be started again later. Calling this before listening has started has no effect.
   * @returns nothing
   */
  stopListeningForPageVisibility(): void {
    if (this.evtname === null) {
      return;
    }

    document.removeEventListener(this.evtname, this.visChange);
    this.evtname = null; // reset so we can enable it back via startListening..
  }

  /**
   * Listeners should execute as fast as possible.
   *
   * It is the responsibility of the listener to implement its own debounce logic so to avoid unnecessary repetitive
   * execution.
   *
   * At the moment, debounce will not be implemented here to allow the listeners to react in the shortest
   * possible time.
   * @param listener - the listener to call on every visibility change
   * @returns nothing
   */
  registerVisibilityListener(listener: VisibilityListener): void {
    this.visibilityListeners.push(listener);
  }

  /**
   * Removes a previously registered listener.
   * @param listener - the same reference that was passed to
   * {@link PageVisibilityService.registerVisibilityListener}
   * @returns nothing
   */
  removeVisibilityListener(listener: VisibilityListener): void {
    this.visibilityListeners = this.visibilityListeners.filter(l => l !== listener);
  }

}
